import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../services/api/axios";

const LeadStatusRenderer = ({ value, data, onStatusChange, onRowUpdate }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const dropdownRef = useRef(null);

    // Debug logging
    console.log('LeadStatusRenderer rendered with:', { value, data });

    const statusOptions = [
        { 
            value: "NO_INTENT", 
            label: "No Intent", 
            color: "bg-gray-100 text-gray-700 border-gray-200",
            dotColor: "bg-gray-400"
        },
        { 
            value: "LOW_INTENT", 
            label: "Low Intent", 
            color: "bg-yellow-100 text-yellow-800 border-yellow-200",
            dotColor: "bg-yellow-400"
        },
        { 
            value: "HIGH_INTENT", 
            label: "High Intent", 
            color: "bg-orange-100 text-orange-800 border-orange-200",
            dotColor: "bg-orange-400"
        },
        { 
            value: "NEGOTIATION", 
            label: "Negotiation", 
            color: "bg-red-100 text-red-800 border-red-200",
            dotColor: "bg-red-400"
        }
    ];

    const getStatusConfig = (statusValue) => {
        return statusOptions.find(option => option.value === statusValue) || statusOptions[0];
    };

    // Handle case where value might be null/undefined
    const displayStatus = value || "NO_INTENT";
    const currentStatusConfig = getStatusConfig(displayStatus);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't close if clicking on dropdown elements
            if (event.target.closest('[data-dropdown="true"]')) {
                return;
            }
            
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                console.log('Click outside detected, closing dropdown');
                setIsOpen(false);
            }
        };

        if (isOpen) {
            // Use a small delay to prevent immediate closure
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    const handleStatusChange = async (newStatus) => {
        console.log('handleStatusChange called with:', newStatus, 'current value:', value);
        
        if (newStatus === value) {
            console.log('Status unchanged, closing dropdown');
            setIsOpen(false);
            return;
        }

        const leadData = data?.user_account;
        if (!leadData) {
            console.error('Missing lead data:', data);
            toast.error("Missing lead data");
            setIsOpen(false);
            return;
        }

        // Validate required parameters
        if (!leadData.user?.userId || !leadData.customer?.customerId || !leadData.project?.projectId) {
            toast.error("Missing required data to update lead status");
            setIsOpen(false);
            return;
        }

        setIsUpdating(true);
        try {
            const payload = {
                customerId: leadData.customer.customerId,
                userId: leadData.user.userId,
                personaId: leadData.personaId || 2,
                projectId: leadData.project.projectId,
                comment: leadData.comment || {},
                status: leadData.status || "OPEN",
                leadStatus: newStatus,
                propertyConfigId: leadData.propertyConfig?.propertyConfigId || null,
                lastMessageSentTimestamp: leadData.lastMessageSentTimestamp || new Date().toISOString().slice(0, -1) ,
                lastReplyTimestamp: leadData.lastReplyTimestamp || new Date().toISOString().slice(0, -1) 
            };

            console.log('Making API call with payload:', payload);
            await axiosInstance.put("/customers/user", payload);
            console.log('API call successful');
            
            toast.success(`Lead status updated to ${getStatusConfig(newStatus).label}`);
            setIsOpen(false);
            
            // Update only the specific row instead of refetching entire table
            if (onRowUpdate) {
                onRowUpdate(data, newStatus);
            } else if (onStatusChange) {
                // Fallback to full refresh if onRowUpdate not provided
                onStatusChange();
            }
        } catch (error) {
            console.error("Error updating lead status:", error);
            toast.error("Failed to update lead status");
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Status Button */}
            <button
                className={`
                    px-3 py-1.5 rounded-lg text-xs font-medium 
                    flex items-center gap-2 transition-all duration-200
                    border hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                    ${currentStatusConfig.color} ${currentStatusConfig.color.includes('border') ? 'border' : 'border-transparent'}
                    ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
                `}
                onClick={() => !isUpdating && setIsOpen(!isOpen)}
                disabled={isUpdating}
                title="Click to change lead status"
            >
                {isUpdating ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                    <>
                        <div className={`w-2 h-2 rounded-full ${currentStatusConfig.dotColor}`}></div>
                        <span>{currentStatusConfig.label}</span>
                        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </>
                )}
            </button>

            {/* Dropdown Menu - Using Portal */}
            {isOpen && createPortal(
                <>
                    {/* Backdrop */}
                    <div 
                        data-dropdown="true"
                        className="fixed inset-0 bg-black bg-opacity-10"
                        style={{ zIndex: 9998 }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsOpen(false);
                        }}
                    />
                    
                    {/* Dropdown */}
                    <div 
                        data-dropdown="true"
                        className="fixed w-56 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                        style={{ 
                            zIndex: 9999,
                            top: '0px',
                            left: '0px'
                        }}
                        ref={(el) => {
                            if (el && dropdownRef.current) {
                                const rect = dropdownRef.current.getBoundingClientRect();
                                el.style.top = `${rect.bottom + 8}px`;
                                el.style.left = `${rect.left}px`;
                                
                                // Ensure dropdown stays within viewport
                                const dropdownRect = el.getBoundingClientRect();
                                if (dropdownRect.right > window.innerWidth) {
                                    el.style.left = `${window.innerWidth - dropdownRect.width - 10}px`;
                                }
                                if (dropdownRect.bottom > window.innerHeight) {
                                    el.style.top = `${rect.top - dropdownRect.height - 8}px`;
                                }
                            }
                        }}
                    >
                        <div className="py-2">
                            <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                                Change Lead Status
                            </div>
                            {statusOptions.map((option) => (
                                <button
                                    key={option.value}
                                    className={`
                                        w-full text-left px-3 py-2.5 text-sm 
                                        flex items-center gap-3 transition-colors duration-150
                                        ${option.value === value 
                                            ? "bg-blue-50 text-blue-700 border-r-2 border-blue-500" 
                                            : "text-gray-700 hover:bg-gray-50"
                                        }
                                        ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                    `}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isUpdating) {
                                        handleStatusChange(option.value);
                                    }
                                }}
                                disabled={isUpdating}
                                >
                                    <div className={`w-3 h-3 rounded-full ${option.dotColor}`}></div>
                                    <span className="font-medium">{option.label}</span>
                                    {option.value === value && (
                                        <div className="ml-auto">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export default LeadStatusRenderer;
